//Filter.tsx
export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterCategory {
  label: string;
  options: FilterOption[];
}

export interface FilterData {
  courseInfo: {
    fieldOfStudy: FilterCategory;
    major: FilterCategory;
    level: FilterCategory;
  };
  financials: {
    paymentSchemes: FilterCategory;
    intakeMonth: FilterCategory;
    partTimeOption: FilterCategory;
    durationAbroad: FilterCategory;
  };
  location: {
    country: FilterCategory;
    state: FilterCategory;
    institution: FilterCategory;
    environment: FilterCategory;
    publicTransport: FilterCategory;
    overseasTransfer: FilterCategory;
  };
}

export interface MajorOptions {
  [fieldOfStudy: string]: FilterOption[];
}

export interface StateOptions {
  [country: string]: FilterOption[];
}

export const initialState = {
  courseInfo: {
    selectedFieldOfStudy: "",
    selectedMajor: "",
    selectedLevel: "",
  },
  financials: {
    feeBudget: 1350000,
    fullTimeDuration: 7,
    paymentSchemes: "",
    intakeMonth: "",
    partTimeOption: "",
    durationAbroad: "",
  },
  location: {
    selectedCountry: "",
    selectedState: "",
    selectedInstitution: "",
    selectedEnvironment: "",
    selectedPublicTransport: "",
    selectedOverseasTransfer: "",
  },
};

export const majorOptions: MajorOptions = {
  "accounting-finance": [
    { value: "acca", label: "ACCA" },
    { value: "accafia", label: "ACCA Foundation in Accountancy (ACCA FiA)" },
    { value: "accounting", label: "Accounting" },
    { value: "accounting-finance", label: "Accounting and Finance" },
    { value: "audit", label: "Audit" },
    { value: "banking", label: "Banking" },
    { value: "banking-finance", label: "Banking and Finance" },
    { value: "cfab", label: "CFAB" },
    { value: "cima", label: "CIMA" },
    { value: "economics", label: "Economics" },
    { value: "economics-finance", label: "Economics and Finance" },
    { value: "entrepreneurship", label: "Entrepreneurship" },
    { value: "finance", label: "Finance" },
    { value: "financial-technology", label: "Financial Technology (Fintech)" },
    { value: "forensic-accounting", label: "Forensic Accounting" },
    { value: "icaew", label: "ICAEW" },
    { value: "investment", label: "Investment" },
    { value: "islamic-finance", label: "Islamic Finance" },
    { value: "law", label: "Law" },
    { value: "management", label: "Management" },
    { value: "banking", label: "Banking" },
    { value: "professional-accounting", label: "Professional Accounting" },
    { value: "taxation", label: "Taxation" },
  ],
  "architecture-built-environment": [
    { value: "architecture", label: "Architecture" },
    { value: "construction-management", label: "Construction Management" },
    { value: "interior-architecture", label: "Interior Architecture" },
    { value: "interior-design", label: "Interior Design" },
    { value: "landscape-architecture", label: "Landscape Architecture" },
    { value: "quantity-surveying", label: "Quantity Surveying" },
    { value: "urban-planning", label: "Urban Planning" },
  ],
  business: [
    { value: "accounting", label: "Accounting" },
    { value: "actuarial-science", label: "Actuarial Science" },
    { value: "advertising", label: "Advertising" },
    { value: "banking", label: "Banking" },
    { value: "business-administration", label: "Business Administration" },
    { value: "business-information", label: "Business Information" },
    { value: "business-management", label: "Business and Management" },
    { value: "communication", label: "Communication" },
    { value: "construction-management", label: "Construction Management" },
    { value: "data-science-management", label: "Data Science Management" },
    { value: "digital-business", label: "Digital Business" },
    { value: "digital-health", label: "Digital Health" },
    { value: "digital-marketing", label: "Digital Marketing" },
    { value: "ecommerce", label: "E-commerce" },
    { value: "economics", label: "Economics" },
    { value: "enterprise", label: "Enterprise" },
    { value: "entrepreneurship", label: "Entrepreneurship" },
    { value: "finance", label: "Finance" },
    { value: "healthcare-management", label: "Healthcare Management" },
    { value: "human-resource-management", label: "Human Resource Management" },
    { value: "international-business", label: "International Business" },
    { value: "investment", label: "Investment" },
    { value: "law", label: "Law" },
    {
      value: "logistic-supply-chain-management",
      label: "Logistic and Supply Chain Management",
    },
    { value: "mba", label: "MBA Programme" },
    { value: "management", label: "Management" },
    { value: "marketing", label: "Marketing" },
    { value: "project-management", label: "Project Management" },
    { value: "property-real-estate", label: "Property and Real Estate" },
    { value: "psychology", label: "Psychology" },
    { value: "public-relations", label: "Public Relations" },
    { value: "retail-management", label: "Retail Management" },
    { value: "skm", label: "Sijil Kemahiran Malaysia (SKM)" },
    { value: "sports-management", label: "Sports Management" },
    { value: "technopreneurship", label: "Technopreneurship" },
  ],
  "communication-media-studies": [
    { value: "advertising", label: "Advertising" },
    { value: "animation", label: "Animation" },
    { value: "branding", label: "Branding" },
    { value: "broadcasting", label: "Broadcasting" },
    { value: "communication", label: "Communication" },
    { value: "creative-arts", label: "Creative Arts" },
    { value: "design", label: "Design" },
    { value: "digital-media", label: "Digital Media" },
    { value: "event-management", label: "Event Management" },
    { value: "film-television", label: "Film and Television" },
    { value: "games-development", label: "Games Development" },
    { value: "graphic-design", label: "Graphic Design" },
    { value: "journalism", label: "Journalism" },
    { value: "management", label: "Management" },
    { value: "marketing", label: "Marketing" },
    { value: "mass-communication", label: "Mass Communication" },
    { value: "media-studies", label: "Media Studies" },
    { value: "multimedia-design", label: "Multimedia Design" },
    { value: "photography", label: "Photography" },
    {
      value: "professional-communication",
      label: "Professional Communication",
    },
    { value: "psychology", label: "Psychology" },
    { value: "public-relations", label: "Public Relations" },
    { value: "ui-design", label: "User Interface (UI) Design" },
    { value: "virtual-reality", label: "Virtual Reality" },
    { value: "visual-effects", label: "Visual Effects" },
  ],
  "computing-it": [
    { value: "analytics", label: "Analytics" },
    { value: "artificial-intelligence", label: "Artificial Intelligence" },
    { value: "business-information", label: "Business Information" },
    { value: "cloud-computing", label: "Cloud Computing" },
    { value: "communication-technology", label: "Communication Technology" },
    { value: "computer-science", label: "Computer Science" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "data-science", label: "Data Science" },
    { value: "database-administration", label: "Database Administration" },
    { value: "ecommerce", label: "E-commerce" },
    { value: "entrepreneurship", label: "Entrepreneurship" },
    { value: "forensic-computing", label: "Forensic Computing" },
    { value: "games-development", label: "Games Development" },
    { value: "information-security", label: "Information Security" },
    { value: "information-systems", label: "Information Systems" },
    { value: "information-technology", label: "Information Technology (IT)" },
    { value: "intelligent-systems", label: "Intelligent Systems" },
    { value: "internet-technology", label: "Internet Technology" },
    { value: "iot", label: "Internet of Things (IoT)" },
    { value: "mathematics", label: "Mathematics" },
    { value: "mobile-computing", label: "Mobile Computing" },
    { value: "multimedia-design", label: "Multimedia Design" },
    { value: "network-technology", label: "Network Technology" },
    { value: "skm", label: "Sijil Kemahiran Malaysia (SKM)" },
    { value: "software-development", label: "Software Development" },
    { value: "software-engineering", label: "Software Engineering" },
  ],
  "creative-arts-design": [
    { value: "animation", label: "Animation" },
    { value: "classical-music", label: "Classical Music" },
    { value: "contemporary-art", label: "Contemporary Art" },
    { value: "contemporary-music", label: "Contemporary Music" },
    { value: "creative-arts", label: "Creative Arts" },
    { value: "digital-media", label: "Digital Media" },
    { value: "fashion-design", label: "Fashion Design" },
    { value: "fine-art", label: "Fine Art" },
    { value: "graphic-design", label: "Graphic Design" },
    { value: "illustration", label: "Illustration" },
    { value: "industrial-design", label: "Industrial Design" },
    { value: "make-up-hair-design", label: "Make-Up and Hair Design" },
    { value: "music", label: "Music" },
    { value: "packaging", label: "Packaging" },
    { value: "performing-arts", label: "Performing Arts" },
    { value: "transportation", label: "Transportation" },
    { value: "visual-art", label: "Visual Art" },
  ],
  education: [
    { value: "chinese", label: "Chinese" },
    { value: "counselling", label: "Counselling" },
    { value: "early-childhood-education", label: "Early Childhood Education" },
    { value: "education", label: "Education" },
    { value: "english", label: "English (Teaching)" },
    { value: "healthcare", label: "Healthcare" },
    { value: "skm", label: "Sijil Kemahiran Malaysia (SKM)" },
    { value: "special-needs-education", label: "Special Needs Education" },
    { value: "teaching", label: "Teaching" },
  ],
  engineering: [
    { value: "aeronautical-engineering", label: "Aeronautical Engineering" },
    {
      value: "aircraft-maintenance-engineering",
      label: "Aircraft Maintenance Engineering",
    },
    { value: "automotive-engineering", label: "Automotive Engineering" },
    { value: "automotive-technology", label: "Automotive Technology" },
    { value: "biomedical-engineering", label: "Biomedical Engineering" },
    { value: "chemical-engineering", label: "Chemical Engineering" },
    { value: "civil-engineering", label: "Civil Engineering" },
    {
      value: "computer-systems-engineering",
      label: "Computer Systems Engineering",
    },
    {
      value: "electrical-electronic-engineering",
      label: "Electrical and Electronic Engineering",
    },
    { value: "electrical-engineering", label: "Electrical Engineering" },
    { value: "electronic-engineering", label: "Electronic Engineering" },
    { value: "environmental-engineering", label: "Environmental Engineering" },
    {
      value: "general-engineering-technology",
      label: "General Engineering and Technology",
    },
    { value: "geomatic-engineering", label: "Geomatic Engineering" },
    { value: "industrial-design", label: "Industrial Design" },
    { value: "industrial-engineering", label: "Industrial Engineering" },
    { value: "management", label: "Management" },
    { value: "manufacturing-engineering", label: "Manufacturing Engineering" },
    {
      value: "manufacturing-production",
      label: "Manufacturing and Production",
    },
    { value: "marine-engineering", label: "Marine Engineering" },
    { value: "material-engineering", label: "Material Engineering" },
    { value: "mechanical-engineering", label: "Mechanical Engineering" },
    { value: "mechatronics-engineering", label: "Mechatronics Engineering" },
    { value: "microelectronics-design", label: "Microelectronics Design" },
    { value: "nanotechnology", label: "Nanotechnology" },
    { value: "oil-gas-operations", label: "Oil and Gas Operations" },
    { value: "optical-engineering", label: "Optical Engineering" },
    { value: "petrochemical", label: "Petrochemical" },
    { value: "petroleum-engineering", label: "Petroleum Engineering" },
    {
      value: "renewable-energy-engineering",
      label: "Renewable Energy Engineering",
    },
    { value: "robotics-engineering", label: "Robotics Engineering" },
    { value: "skm", label: "Sijil Kemahiran Malaysia (SKM)" },
    { value: "telecommunications", label: "Telecommunications" },
  ],
  foundation: [
    { value: "arts-foundation", label: "Foundation in Arts" },
    { value: "science-foundation", label: "Foundation in Science" },
  ],
  "hospitality-services": [
    { value: "airline-services", label: "Airline Services" },
    { value: "culinary-arts", label: "Culinary Arts" },
    { value: "culinology", label: "Culinology" },
    { value: "event-management", label: "Event Management" },
    {
      value: "fnb-preparation-production",
      label: "F&B Preparation and Production",
    },
    { value: "hospitality-management", label: "Hospitality Management" },
    {
      value: "hospitality-tourism-management",
      label: "Hospitality and Tourism Management",
    },
    { value: "hotel-management", label: "Hotel Management" },
    { value: "marketing", label: "Marketing" },
    {
      value: "patisserie-gastronomic-cuisine",
      label: "Patisserie & Gastronomic Cuisine",
    },
    { value: "restaurant-management", label: "Restaurant Management" },
    { value: "skm", label: "Sijil Kemahiran Malaysia (SKM)" },
    { value: "tourism-management", label: "Tourism Management" },
  ],
  "international-pre-university": [
    {
      value: "wace",
      label: "Western Australian Certificate of Education Board (WACE)",
    },

    { value: "a-levels", label: "A-Levels" },
    {
      value: "american-degree-transfer-programme",
      label: "American Degree Transfer Programme",
    },
    {
      value: "australian-degree-transfer-programme",
      label: "Australian Degree Transfer Programme",
    },
    { value: "ausmat", label: "Australian Matriculation (AUSMAT)" },
    { value: "canadian-matriculation", label: "Canadian Matriculation" },
    {
      value: "international-baccalaureate-diploma",
      label: "International Baccalaureate (IB Diploma)",
    },
    {
      value: "international-baccalaureate",
      label: "International Baccalaureate (IB)",
    },
    { value: "mufy", label: "Monash University Foundation Year (MUFY)" },
    {
      value: "sace",
      label: "South Australian Certificate of Education (SACE)",
    },
    { value: "transition-program", label: "Transition Program" },
  ],
  "language-social-studies": [
    { value: "chinese-studies", label: "Chinese Studies" },
    { value: "cognitive-neuroscience", label: "Cognitive Neuroscience" },
    { value: "communication", label: "Communication" },
    { value: "counselling", label: "Counselling" },
    { value: "economics", label: "Economics" },
    { value: "english", label: "English" },
    { value: "gender-studies", label: "Gender Studies" },
    { value: "ielts", label: "IELTS" },
    { value: "international-relations", label: "International Relations" },
    { value: "language-studies", label: "Language Studies" },
    { value: "literature", label: "Literature" },
    { value: "malay-studies", label: "Malay Studies" },
    { value: "management", label: "Management" },
    { value: "philosophy", label: "Philosophy" },
    { value: "police-science", label: "Police Science" },
    { value: "policy-science", label: "Policy Science" },
    { value: "politics", label: "Politics" },
    { value: "psychology", label: "Psychology" },
    { value: "public-relations", label: "Public Relations" },
    { value: "social-sciences", label: "Social Sciences" },
    { value: "translation-studies", label: "Translation Studies" },
    { value: "writing", label: "Writing" },
  ],
  law: [
    { value: "business-law", label: "Business Law" },
    { value: "clp", label: "Certificate in Legal Practice (CLP)" },
    { value: "commercial-law", label: "Commercial Law" },
    { value: "construction-law", label: "Construction Law" },
    { value: "employment-law", label: "Employment Law" },
    { value: "government-politics-law", label: "Government and Politics Law" },
    { value: "healthcare-law", label: "Healthcare Law" },
    { value: "insurance-law", label: "Insurance Law" },
    { value: "intellectual-law", label: "Intellectual Law" },
    { value: "international-law", label: "International Law" },
    { value: "labour-law", label: "Labour Law" },
    { value: "llb", label: "Law (LL.B.)" },
    { value: "oil-gas-law", label: "Oil and Gas Law" },
    {
      value: "uk-law-degree-transfer-programme",
      label: "UK Law Degree Transfer Programme",
    },
  ],
  "medicine-allied-health-sciences": [
    { value: "acupuncture", label: "Acupuncture" },
    { value: "alternative-medicine", label: "Alternative Medicine" },
    { value: "anatomy", label: "Anatomy" },
    { value: "biomedical-science", label: "Biomedical Science" },
    { value: "chiropractic", label: "Chiropractic" },
    { value: "critical-care", label: "Critical Care" },
    { value: "dentistry", label: "Dentistry" },
    { value: "dietetics", label: "Dietetics" },
    { value: "forensic-science", label: "Forensic Science" },
    { value: "health-informatics", label: "Health Informatics" },
    { value: "health-studies", label: "Health Studies" },
    { value: "healthcare", label: "Healthcare" },
    { value: "management", label: "Management" },
    { value: "medical-sciences", label: "Medical Sciences" },
    {
      value: "medical-technology",
      label: "Medical Technology",
    },
    { value: "medicine", label: "Medicine" },
    { value: "midwifery", label: "Midwifery" },
    { value: "nursing", label: "Nursing" },
    { value: "nutrition", label: "Nutrition" },
    {
      value: "occupational-safety-health",
      label: "Occupational Safety and Health",
    },
    { value: "occupational-therapy", label: "Occupational Therapy" },
    { value: "oncology", label: "Oncology" },
    { value: "paediatrics", label: "Paediatrics" },
    { value: "paramedic-science", label: "Paramedic Science" },
    { value: "perioperative-care", label: "Perioperative Care" },
    { value: "pharmaceutical-chemistry", label: "Pharmaceutical Chemistry" },
    { value: "pharmaceutical-science", label: "Pharmaceutical Science" },
    { value: "pharmacy", label: "Pharmacy" },
    { value: "physiotherapy", label: "Physiotherapy" },
    { value: "public-health", label: "Public Health" },
    { value: "renal-nursing", label: "Renal Nursing" },
    { value: "sports-science", label: "Sports Science" },
    { value: "surgery", label: "Surgery" },
    {
      value: "traditional-chinese-medicine",
      label: "Traditional Chinese Medicine",
    },
    { value: "veterinary-medicine", label: "Veterinary Medicine" },
  ],
  "science-mathematics": [
    { value: "agriculture", label: "Agriculture" },
    { value: "aquaculture", label: "Aquaculture" },
    { value: "aquatic-science", label: "Aquatic Science" },
    { value: "biochemistry", label: "Biochemistry" },
    { value: "bioinformatics", label: "Bioinformatics" },
    { value: "biology", label: "Biology" },
    { value: "biotechnology", label: "Biotechnology" },
    { value: "chemistry", label: "Chemistry" },
    { value: "computing", label: "Computing" },
    { value: "economics", label: "Economics" },
    { value: "environmental-health", label: "Environmental Health" },
    { value: "environmental-sciences", label: "Environmental Sciences" },
    { value: "financial-mathematics", label: "Financial Mathematics" },
    { value: "food-science", label: "Food Science" },
    { value: "general-sciences-oversea", label: "General Sciences (Oversea)" },
    {
      value: "general-sciences-postgraduate",
      label: "General Sciences (Postgraduate)",
    },
    { value: "geography", label: "Geography" },
    { value: "geology", label: "Geology" },
    { value: "management", label: "Management" },
    { value: "mathematics", label: "Mathematics" },
    { value: "microbiology", label: "Microbiology" },
    { value: "nautical-science", label: "Nautical Science" },
    { value: "physics", label: "Physics" },
    { value: "psychology", label: "Psychology" },
  ],
};

export const stateOptions: StateOptions = {
  malaysia: [
    { value: "johor", label: "Johor" },
    { value: "kedah", label: "Kedah" },
    { value: "kelantan", label: "Kelantan" },
    { value: "melaka", label: "Melaka" },
    { value: "negeri-sembilan", label: "Negeri Sembilan" },
    { value: "pahang", label: "Pahang" },
    { value: "pulau-pinang", label: "Pulau Pinang" },
    { value: "perak", label: "Perak" },
    { value: "perlis", label: "Perlis" },
    { value: "selangor", label: "Selangor" },
    { value: "terrengganu", label: "Terengganu" },
    { value: "sabah", label: "Sabah" },
    { value: "sarawak", label: "Sarawak" },
    { value: "kuala-lumpur", label: "Wilayah Persekutuan Kuala Lumpur" },
    { value: "labuan", label: "Wilayah Persekutuan Labuan" },
    { value: "putrajaya", label: "Wilayah Persekutuan Putrajaya" },
  ],
  singapore: [
    // { value: "central", label: "Central Singapore" },
    // { value: "north-east", label: "North East" },
    // { value: "north-west", label: "North West" },
    // { value: "south-east", label: "South East" },
    // { value: "south-west", label: "South West" },
  ],
  "united-kingdom": [
    { value: "aberdeenshire", label: "Aberdeenshire" },
    { value: "aberdeen-city", label: "Aberdeen City" },
    { value: "argyll-and-bute", label: "Argyll and Bute" },
    {
      value: "isle-of-anglesey",
      label: "Isle of Anglesey [Sir Ynys Mon GB-YNM]",
    },
    { value: "angus", label: "Angus" },
    { value: "antrim", label: "Antrim" },
    { value: "ards", label: "Ards" },
    { value: "armagh", label: "Armagh" },
    {
      value: "bath-and-north-east-somerset",
      label: "Bath and North East Somerset",
    },
    { value: "blackburn-with-darwen", label: "Blackburn with Darwen" },
    { value: "bedfordshire", label: "Bedfordshire" },
    { value: "barking-and-dagham", label: "Barking and Dagenham" },
    { value: "brent", label: "Brent" },
    { value: "bexley", label: "Bexley" },
    { value: "belfast", label: "Belfast" },
    { value: "bridgend", label: "Bridgend [Pen-y-bont ar Ogwr GB-POG]" },
    { value: "blaenau-gwent", label: "Blaenau Gwent" },
    { value: "birmingham", label: "Birmingham" },
    { value: "buckinghamshire", label: "Buckinghamshire" },
    { value: "ballymena", label: "Ballymena" },
    { value: "ballymoney", label: "Ballymoney" },
    { value: "bournemouth", label: "Bournemouth" },
    { value: "banbridge", label: "Banbridge" },
    { value: "barnet", label: "Barnet" },
    { value: "brighton-and-hove", label: "Brighton and Hove" },
    { value: "barnsley", label: "Barnsley" },
    { value: "bolton", label: "Bolton" },
    { value: "blackpool", label: "Blackpool" },
    { value: "bracknell-forest", label: "Bracknell Forest" },
    { value: "bradford", label: "Bradford" },
    { value: "bromley", label: "Bromley" },
    { value: "bristol-city", label: "Bristol, City of" },
    { value: "bury", label: "Bury" },
    { value: "cambridgeshire", label: "Cambridgeshire" },
    { value: "caerphilly", label: "Caerphilly [Caerffili GB-CAF]" },
    { value: "ceredigion", label: "Ceredigion [Sir Ceredigion]" },
    { value: "craigavon", label: "Craigavon" },
    { value: "cheshire", label: "Cheshire" },
    { value: "carrickfergus", label: "Carrickfergus" },
    { value: "cookstown", label: "Cookstown" },
    { value: "calderdale", label: "Calderdale" },
    { value: "clackmannanshire", label: "Clackmannanshire" },
    { value: "coleraine", label: "Coleraine" },
    { value: "cumbria", label: "Cumbria" },
    { value: "camden", label: "Camden" },
    {
      value: "carmarthenshire",
      label: "Carmarthenshire [Sir Gaerfyrddin GB-GFY]",
    },
    { value: "cornwall", label: "Cornwall" },
    { value: "coventry", label: "Coventry" },
    { value: "cardiff", label: "Cardiff [Caerdydd GB-CRD]" },
    { value: "croydon", label: "Croydon" },
    { value: "castlereagh", label: "Castlereagh" },
    { value: "conwy", label: "Conwy" },
    { value: "darlington", label: "Darlington" },
    { value: "derbyshire", label: "Derbyshire" },
    { value: "denbighshire", label: "Denbighshire [Sir Ddinbych GB-DDB]" },
    { value: "derby", label: "Derby" },
    { value: "devon", label: "Devon" },
    { value: "dungannon", label: "Dungannon" },
    { value: "dumfries-and-galloway", label: "Dumfries and Galloway" },
    { value: "doncaster", label: "Doncaster" },
    { value: "dundee-city", label: "Dundee City" },
    { value: "dorset", label: "Dorset" },
    { value: "down", label: "Down" },
    { value: "derry", label: "Derry" },
    { value: "dudley", label: "Dudley" },
    { value: "durham", label: "Durham" },
    { value: "ealing", label: "Ealing" },
    { value: "east-ayrshire", label: "East Ayrshire" },
    { value: "edinburgh-city", label: "Edinburgh, City of" },
    { value: "east-dunbartonshire", label: "East Dunbartonshire" },
    { value: "east-lothian", label: "East Lothian" },
    { value: "eilean-siar", label: "Eilean Siar" },
    { value: "enfield", label: "Enfield" },
    { value: "east-renfrewshire", label: "East Renfrewshire" },
    { value: "east-riding-of-yorkshire", label: "East Riding of Yorkshire" },
    { value: "essex", label: "Essex" },
    { value: "east-sussex", label: "East Sussex" },
    { value: "falkirk", label: "Falkirk" },
    { value: "fermanagh", label: "Fermanagh" },
    { value: "fife", label: "Fife" },
    { value: "flintshire", label: "Flintshire [Sir y Fflint GB-FFL]" },
    { value: "gateshead", label: "Gateshead" },
    { value: "glasgow-city", label: "Glasgow City" },
    { value: "gloucestershire", label: "Gloucestershire" },
    { value: "greenwich", label: "Greenwich" },
    { value: "gwynedd", label: "Gwynedd" },
    { value: "halton", label: "Halton" },
    { value: "hampshire", label: "Hampshire" },
    { value: "havering", label: "Havering" },
    { value: "hackney", label: "Hackney" },
    { value: "herefordshire", label: "Herefordshire, County of" },
    { value: "hillingdon", label: "Hillingdon" },
    { value: "hammersmith-and-fulham", label: "Hammersmith and Fulham" },
    { value: "hounslow", label: "Hounslow" },
    { value: "hartlepool", label: "Hartlepool" },
    { value: "hertfordshire", label: "Hertfordshire" },
    { value: "harrow", label: "Harrow" },
    { value: "haringey", label: "Haringey" },
    { value: "isles-of-scilly", label: "Isles of Scilly" },
    { value: "isle-of-wight", label: "Isle of Wight" },
    { value: "islington", label: "Islington" },
    { value: "inverclyde", label: "Inverclyde" },
    { value: "kensington-and-chelsea", label: "Kensington and Chelsea" },
    { value: "kent", label: "Kent" },
    { value: "kingston-upon-hull-city", label: "Kingston upon Hull, City of" },
    { value: "kirklees", label: "Kirklees" },
    { value: "kingston-upon-thames", label: "Kingston upon Thames" },
    { value: "knowsley", label: "Knowsley" },
    { value: "lancashire", label: "Lancashire" },
    { value: "lambeth", label: "Lambeth" },
    { value: "leicester", label: "Leicester" },
    { value: "leeds", label: "Leeds" },
    { value: "leicestershire", label: "Leicestershire" },
    { value: "lewisham", label: "Lewisham" },
    { value: "lincolnshire", label: "Lincolnshire" },
    { value: "liverpool", label: "Liverpool" },
    { value: "limavady", label: "Limavady" },
    { value: "london-city", label: "London, City of" },
    { value: "larne", label: "Larne" },
    { value: "lisburn", label: "Lisburn" },
    { value: "luton", label: "Luton" },
    { value: "manchester", label: "Manchester" },
    { value: "middlesbrough", label: "Middlesbrough" },
    { value: "medway", label: "Medway" },
    { value: "magherafelt", label: "Magherafelt" },
    { value: "milton-keynes", label: "Milton Keynes" },
    { value: "midlothian", label: "Midlothian" },
    { value: "monmouthshire", label: "Monmouthshire [Sir Fynwy GB-FYN]" },
    { value: "merton", label: "Merton" },
    { value: "moray", label: "Moray" },
    {
      value: "merthyr-tydfil",
      label: "Merthyr Tydfil [Merthyr Tudful GB-MTU]",
    },
    { value: "moyle", label: "Moyle" },
    { value: "north-ayrshire", label: "North Ayrshire" },
    { value: "northumberland", label: "Northumberland" },
    { value: "north-down", label: "North Down" },
    { value: "north-east-lincolnshire", label: "North East Lincolnshire" },
    { value: "newcastle-upon-tyne", label: "Newcastle upon Tyne" },
    { value: "norfolk", label: "Norfolk" },
    { value: "nottingham", label: "Nottingham" },
    { value: "north-lanarkshire", label: "North Lanarkshire" },
    { value: "north-lincolnshire", label: "North Lincolnshire" },
    { value: "north-somerset", label: "North Somerset" },
    { value: "newtownabbey", label: "Newtownabbey" },
    { value: "northamptonshire", label: "Northamptonshire" },
    {
      value: "neath-port-talbot",
      label: "Neath Port Talbot [Castell-nedd Port Talbot GB-CTL]",
    },
    { value: "nottinghamshire", label: "Nottinghamshire" },
    { value: "north-tyneside", label: "North Tyneside" },
    { value: "newham", label: "Newham" },
    { value: "newport", label: "Newport [Casnewydd GB-CNW]" },
    { value: "north-yorkshire", label: "North Yorkshire" },
    { value: "newry-and-mourne", label: "Newry and Mourne" },
    { value: "oldham", label: "Oldham" },
    { value: "omagh", label: "Omagh" },
    { value: "orkney-islands", label: "Orkney Islands" },
    { value: "oxfordshire", label: "Oxfordshire" },
    { value: "pembrokeshire", label: "Pembrokeshire [Sir Benfro GB-BNF]" },
    { value: "perth-and-kinross", label: "Perth and Kinross" },
    { value: "plymouth", label: "Plymouth" },
    { value: "poole", label: "Poole" },
    { value: "portsmouth", label: "Portsmouth" },
    { value: "powys", label: "Powys" },
    { value: "peterborough", label: "Peterborough" },
    { value: "redcar-and-cleveland", label: "Redcar and Cleveland" },
    { value: "rochdale", label: "Rochdale" },
    {
      value: "rhondda-cynon-taff",
      label: "Rhondda, Cynon, Taff [Rhondda, Cynon, Taff]",
    },
    { value: "redbridge", label: "Redbridge" },
    { value: "reading", label: "Reading" },
    { value: "renfrewshire", label: "Renfrewshire" },
    { value: "richmond-upon-thames", label: "Richmond upon Thames" },
    { value: "rotherham", label: "Rotherham" },
    { value: "rutland", label: "Rutland" },
    { value: "sandwell", label: "Sandwell" },
    { value: "south-ayrshire", label: "South Ayrshire" },
    { value: "scottish-borders", label: "Scottish Borders, The" },
    { value: "suffolk", label: "Suffolk" },
    { value: "sefton", label: "Sefton" },
    { value: "south-gloucestershire", label: "South Gloucestershire" },
    { value: "sheffield", label: "Sheffield" },
    { value: "st-helens", label: "St.Helens" },
    { value: "shropshire", label: "Shropshire" },
    { value: "stockport", label: "Stockport" },
    { value: "salford", label: "Salford" },
    { value: "slough", label: "Slough" },
    { value: "south-lancashire", label: "South Lanarkshire" },
    { value: "sunderland", label: "Sunderland" },
    { value: "solihull", label: "Solihull" },
    { value: "somerset", label: "Somerset" },
    { value: "southend-on-sea", label: "Southend-on-Sea" },
    { value: "surrey", label: "Surrey" },
    { value: "strabane", label: "Strabane" },
    { value: "stoke-on-trent", label: "Stoke-on-Trent" },
    { value: "stirling", label: "Stirling" },
    { value: "southampton", label: "Southampton" },
    { value: "sutton", label: "Sutton" },
    { value: "staffordshire", label: "Staffordshire" },
    { value: "stockton-on-tees", label: "Stockton-on-Tees" },
    { value: "south-tyneside", label: "South Tyneside" },
    { value: "swansea", label: "Swansea [Abertawe GB-ATA]" },
    { value: "swindon", label: "Swindon" },
    { value: "southwark", label: "Southwark" },
    { value: "tameside", label: "Tameside" },
    { value: "telford-and-wrekin", label: "Telford and Wrekin" },
    { value: "thurrock", label: "Thurrock" },
    { value: "torbay", label: "Torbay" },
    { value: "torfaen", label: "Torfaen [Tor-faen]" },
    { value: "torridge", label: "Torridge" },
    { value: "tower-hamlets", label: "Tower Hamlets" },
    {
      value: "vale-of-glamorgan",
      label: "Vale of Glamorgan, The [Bro Morgannwg GB-BMG]",
    },
    { value: "warwickshire", label: "Warwickshire" },
    { value: "west-berkshire", label: "West Berkshire" },
    { value: "west-dunbartonshire", label: "West Dunbartonshire" },
    { value: "waltham-forest", label: "Waltham Forest" },
    { value: "wigan", label: "Wigan" },
    { value: "wiltshire", label: "Wiltshire" },
    { value: "wakefield", label: "Wakefield" },
    { value: "walsall", label: "Walsall" },
    { value: "west-lothian", label: "West Lothian" },
    { value: "wolverhampton", label: "Wolverhampton" },
    { value: "wandsworth", label: "Wandsworth" },
    { value: "windsor-and-maidenhead", label: "Windsor and Maidenhead" },
    { value: "wokingham", label: "Wokingham" },
    { value: "worcestershire", label: "Worcestershire" },
    { value: "wirral", label: "Wirral" },
    { value: "warrington", label: "Warrington" },
    { value: "wrexham", label: "Wrexham [Wrecsam GB-WRC]" },
    { value: "westminster", label: "Westminster" },
    { value: "west-sussex", label: "West Sussex" },
    { value: "york", label: "York" },
    { value: "shetland-islands", label: "Shetland Islands" },
  ],
  china: [
    { value: "beijing", label: "Beijing" },
    { value: "tianjin", label: "Tianjin" },
    { value: "hebei", label: "Hebei" },
    { value: "shanxi", label: "Shanxi" },
    { value: "nei-mongol", label: "Nei Mongol (mn)" },
    { value: "liaoning", label: "Liaoning" },
    { value: "jilin", label: "Jilin" },
    { value: "heilongjiang", label: "Heilongjiang" },
    { value: "shanghai", label: "Shanghai" },
    { value: "jiangsu", label: "Jiangsu" },
    { value: "zhejiang", label: "Zhejiang" },
    { value: "anhui", label: "Anhui" },
    { value: "fujian", label: "Fujian" },
    { value: "jiangxi", label: "Jiangxi" },
    { value: "shandong", label: "Shandong" },
    { value: "henan", label: "Henan" },
    { value: "hubei", label: "Hubei" },
    { value: "hunan", label: "Hunan" },
    { value: "guangdong", label: "Guangdong" },
    { value: "guangxi", label: "Guangxi" },
    { value: "hainan", label: "Hainan" },
    { value: "chongqing", label: "Chongqing" },
    { value: "sichuan", label: "Sichuan" },
    { value: "guizhou", label: "Guizhou" },
    { value: "yunnan", label: "Yunnan" },
    { value: "xizang", label: "Xizang" },
    { value: "shaanxi", label: "Shaanxi" },
    { value: "gansu", label: "Gansu" },
    { value: "qinghai", label: "Qinghai" },
    { value: "ningxia", label: "Ningxia" },
    { value: "xinjiang", label: "Xinjiang" },
    { value: "taiwan", label: "Taiwan *" },
    { value: "xianggang", label: "Xianggang (zh) **" },
    { value: "aomen", label: "Aomen (zh) ***" },
  ],
  australia: [
    {
      value: "australian-capital-territory",
      label: "Australian Capital Territory",
    },
    { value: "new-south-wales", label: "New South Wales" },
    { value: "northern-territory", label: "Northern Territory" },
    { value: "queensland", label: "Queensland" },
    { value: "south-australia", label: "South Australia" },
    { value: "tasmania", label: "Tasmania" },
    { value: "victoria", label: "Victoria" },
    { value: "western-australia", label: "Western Australia" },
  ],
  ireland: [
    { value: "cork", label: "Cork" },
    { value: "clare", label: "Clare" },
    { value: "cavan", label: "Cavan" },
    { value: "carlowr", label: "Carlow" },
    { value: "dublin", label: "Dublin" },
    { value: "donegal", label: "Donegal" },
    { value: "galway", label: "Galway" },
    { value: "kildare", label: "Kildare" },
    { value: "kilkenny", label: "Kilkenny" },
    { value: "kerry", label: "Kerry" },
    { value: "longford", label: "Longford" },
    { value: "louth", label: "Louth" },
    { value: "limerick", label: "Limerick" },
    { value: "leitrim", label: "Leitrim" },
    { value: "laois", label: "Laois" },
    { value: "meath", label: "Meath" },
    { value: "monaghan", label: "Monaghan" },
    { value: "mayo", label: "Mayo" },
    { value: "offaly", label: "Offaly" },
    { value: "roscommon", label: "Roscommon" },
    { value: "sligo", label: "Sligo" },
    { value: "tipperary", label: "Tipperary" },
    { value: "waterford", label: "Waterford" },
    { value: "westmeath", label: "Westmeath" },
    { value: "wicklow", label: "Wicklow" },
    { value: "wexford", label: "Wexford" },
  ],
  "united-states": [
    { value: "armed-forces-america", label: "Armed Forces America" },
    { value: "armed-forces-europe", label: "Armed Forces Europe" },
    { value: "alaska", label: "Alaska" },
    { value: "alabama", label: "Alabama" },
    { value: "armed-forces-pacific", label: "Armed Forces Pacific" },
    { value: "arkansas", label: "Arkansas" },
    { value: "american-samoa", label: "American Samoa" },
    { value: "arizona", label: "Arizona" },
    { value: "california", label: "California" },
    { value: "colorado", label: "Colorado" },
    { value: "connecticut", label: "Connecticut" },
    { value: "district-of-columbia", label: "District of Columbia" },
    { value: "delaware", label: "Delaware" },
    { value: "florida", label: "Florida" },
    { value: "georgia", label: "Georgia" },
    { value: "hawaii", label: "Hawaii" },
    { value: "iowa", label: "Iowa" },
    { value: "idaho", label: "Idaho" },
    { value: "illinois", label: "Illinois" },
    { value: "indiana", label: "Indiana" },
    { value: "kansas", label: "Kansas" },
    { value: "kentucky", label: "Kentucky" },
    { value: "louisiana", label: "Louisiana" },
    { value: "massachusetts", label: "Massachusetts" },
    { value: "maryland", label: "Maryland" },
    { value: "maine", label: "Maine" },
    { value: "michigan", label: "Michigan" },
    { value: "minnesota", label: "Minnesota" },
    { value: "missouri", label: "Missouri" },
    { value: "northern-mariana-islands", label: "Northern Mariana Islands" },
    { value: "mississippi", label: "Mississippi" },
    { value: "montana", label: "Montana" },
    { value: "north-carolina", label: "North Carolina" },
    { value: "north-dakota", label: "North Dakota" },
    { value: "nebraska", label: "Nebraska" },
    { value: "new-hampshire", label: "New Hampshire" },
    { value: "new-jersey", label: "New Jersey" },
    { value: "new-mexico", label: "New Mexico" },
    { value: "nevada", label: "Nevada" },
    { value: "new-york", label: "New York" },
    { value: "ohio", label: "Ohio" },
    { value: "oklahoma", label: "Oklahoma" },
    { value: "oregon", label: "Oregon" },
    { value: "pennsylvania", label: "Pennsylvania" },
    { value: "puerto-rico", label: "Puerto Rico" },
    { value: "rhode-island", label: "Rhode Island" },
    { value: "south-carolina", label: "South Carolina" },
    { value: "south-dakota", label: "South Dakota" },
    { value: "tennessee", label: "Tennessee" },
    { value: "texas", label: "Texas" },
    {
      value: "united-states-minor-outlying-islands",
      label: "United States Minor Outlying Islands",
    },
    { value: "utah", label: "Utah" },
    { value: "virginia", label: "Virginia" },
    { value: "virgin-islands", label: "Virgin Islands, U.S." },
    { value: "vermont", label: "Vermont" },
    { value: "washington", label: "Washington" },
    { value: "wisconsin", label: "Wisconsin" },
    { value: "west-virginia", label: "West Virginia" },
    { value: "wyoming", label: "Wyoming" },
  ],
  "south-korea": [
    { value: "seoul", label: "Seoul Teukbyeolsi [Seoul-T'ukpyolshi]" },
    { value: "busan", label: "Busan Gwang'yeogsi [Pusan-Kwangyokshi]" },
    { value: "daegu", label: "Daegu Gwang'yeogsi [Taegu-Kwangyokshi]" },
    { value: "incheon", label: "Incheon Gwang'yeogsi [Inch'n-Kwangyokshi]" },
    { value: "gwangju", label: "Gwangju Gwang'yeogsi [Kwangju-Kwangyokshi]" },
    { value: "daejeon", label: "Daejeon Gwang'yeogsi [Taejon-Kwangyokshi]" },
    { value: "ulsan", label: "Ulsan Gwang'yeogsi [Ulsan-Kwangyokshi]" },
    { value: "gyeonggido", label: "Gyeonggido [Kyonggi-do]" },
    { value: "gangweondo", label: "Gang'weondo [Kang-won-do]" },
    {
      value: "chungcheongbugdo",
      label: "Chungcheongbugdo [Ch'ungch'ongbuk-do]",
    },
    {
      value: "chungcheongnamdo",
      label: "Chungcheongnamdo [Ch'ungch'ongnam-do]",
    },
    { value: "jeonrabugdo", label: "Jeonrabugdo [Chollabuk-do]" },
    { value: "jeonranamdo", label: "Jeonranamdo [Chollanam-do]" },
    { value: "gyeongsangbugdo", label: "Gyeongsangbugdo [Kyongsangbuk-do]" },
    { value: "gyeongsangnamdo", label: "Gyeongsangnamdo [Kyongsangnam-do]" },
    { value: "jejudo", label: "Jejudo [Cheju-do]" },
  ],
  switzerland: [
    { value: "aargau", label: "Aargau (de)" },
    { value: "appenzell-innerrhoden", label: "Appenzell Innerrhoden (de)" },
    { value: "appenzell-ausserrhoden", label: "Appenzell Ausserrhoden (de)" },
    { value: "bern", label: "Bern (de)" },
    { value: "basel-landschaft", label: "Basel-Landschaft (de)" },
    { value: "basel-stadt", label: "Basel-Stadt (de)" },
    { value: "fribourg", label: "Fribourg (fr)" },
    { value: "geneva", label: "Geneva (fr)" },
    { value: "glarus", label: "Glarus (de)" },
    { value: "graubunden", label: "Graubünden (de)" },
    { value: "jura", label: "Jura (fr)" },
    { value: "luzern", label: "Luzern (de)" },
    { value: "neuchatel", label: "Neuchatel (fr)" },
    { value: "nidwalden", label: "Nidwalden (de)" },
    { value: "obwalden", label: "Obwalden (de)" },
    { value: "sankt-gallen", label: "Sankt Gallen (de)" },
    { value: "schaffhausen", label: "Schaffhausen (de)" },
    { value: "solothurn", label: "Solothurn (de)" },
    { value: "schwyz", label: "Schwyz (de)" },
    { value: "thurgau", label: "Thurgau (de)" },
    { value: "ticino", label: "Ticino (it)" },
    { value: "uri", label: "Uri (de)" },
    { value: "vaud", label: "Vaud (fr)" },
    { value: "valais", label: "Valais (fr)" },
    { value: "zug", label: "Zug (de)" },
    { value: "zurich", label: "Zurich (de)" },
  ],
};

export const filterData: FilterData = {
  courseInfo: {
    fieldOfStudy: {
      label: "Field of study",
      options: [
        { value: "", label: "Any" },
        { value: "accounting-finance", label: "Accounting and Finance" },
        {
          value: "architecture-built-environment",
          label: "Architecture and Built Environment",
        },
        { value: "business", label: "Business" },
        {
          value: "communication-media-studies",
          label: "Communication and Media Studies",
        },
        { value: "computing-it", label: "Computing and IT" },
        { value: "creative-arts-design", label: "Creative Arts and Design" },
        { value: "education", label: "Education" },
        { value: "engineering", label: "Engineering" },
        { value: "foundation", label: "Foundation" },
        { value: "hospitality-services", label: "Hospitality and Services" },
        {
          value: "international-pre-university",
          label: "International Pre-University",
        },
        {
          value: "language-social-studies",
          label: "Language and Social Studies",
        },
        { value: "law", label: "Law" },
        {
          value: "medicine-allied-health-sciences",
          label: "Medicine and Allied Health Sciences",
        },
        { value: "science-mathematics", label: "Science and Mathematics" },
      ],
    },
    major: {
      label: "Major",
      options: [],
    },
    level: {
      label: "Level",
      options: [
        { value: "", label: "Any" },
        { value: "advanced-diploma", label: "Advanced Diploma" },
        { value: "certificate", label: "Certificate" },
        { value: "degree", label: "Degree" },
        { value: "diploma", label: "Diploma" },
        { value: "foundation-pre-u", label: "Foundation/Pre-U" },
        { value: "postgraduate-diploma", label: "Postgraduate - Diploma" },
        { value: "postgraduate-masters", label: "Postgraduate - Masters" },
        { value: "postgraduate-phd", label: "Postgraduate - PhD" },
        { value: "professional-programmes", label: "Professional Programmes" },
      ],
    },
  },
  financials: {
    paymentSchemes: {
      label: "Payment Schemes",
      options: [
        { value: "", label: "Any" },
        { value: "monthly", label: "Monthly" },
        { value: "bi-monthly", label: "Bi Monthly" },
        { value: "semester", label: "Semester" },
        { value: "annual", label: "Annual" },
      ],
    },
    intakeMonth: {
      label: "Intake Month",
      options: [
        { value: "", label: "Any" },
        { value: "january", label: "January" },
        { value: "february", label: "February" },
        { value: "march", label: "March" },
        { value: "april", label: "April" },
        { value: "may", label: "May" },
        { value: "june", label: "June" },
        { value: "july", label: "July" },
        { value: "august", label: "August" },
        { value: "september", label: "September" },
        { value: "october", label: "October" },
        { value: "november", label: "November" },
        { value: "december", label: "December" },
      ],
    },
    partTimeOption: {
      label: "Part-Time Option",
      options: [
        { value: "", label: "Any" },
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    },
    durationAbroad: {
      label: "Duration Abroad",
      options: [
        { value: "", label: "Any" },
        { value: "none", label: "None" },
        { value: "0-1-year", label: "> 0 - 1 year" },
        { value: "1-2-years", label: "> 1 - 2 years" },
        { value: "2-3-years", label: "> 2 - 3 years" },
        { value: "3-4-years", label: "> 3 - 4 years" },
        { value: "4-years", label: "> 4 years" },
      ],
    },
  },
  location: {
    country: {
      label: "Country",
      options: [
        { value: "", label: "Any" },
        { value: "malaysia", label: "Malaysia" },
        { value: "singapore", label: "Singapore" },
        { value: "united-kingdom", label: "United Kingdom" },
        { value: "australia", label: "Australia" },
        { value: "new-zealand", label: "New Zealand" },
        // { value: "china", label: "China" },
        // { value: "ireland", label: "Ireland" },
        // { value: "united-states", label: "United States" },
        // { value: "south-korea", label: "South Korea" },
        // { value: "switzerland", label: "Switzerland" },
      ],
    },
    state: {
      label: "State",
      options: [],
    },
    institution: {
      label: "Institution",
      options: [
        { value: "", label: "Any" },
        { value: "university", label: "University" },
        { value: "college", label: "College" },
        { value: "school", label: "School" },
      ],
    },
    environment: {
      label: "Environment",
      options: [
        { value: "", label: "Any" },
        { value: "campus-setting", label: "Campus Setting" },
        { value: "urban-lifestyle", label: "Urban Lifestyle" },
        { value: "on-campus-accommodation", label: "On-campus Accommodation" },
      ],
    },
    publicTransport: {
      label: "Public Transport",
      options: [
        { value: "", label: "Any" },
        { value: "by-bus", label: "By Bus" },
        { value: "by-train", label: "By Train" },
      ],
    },
    overseasTransfer: {
      label: "Overseas Transfer",
      options: [
        { value: "", label: "Any" },
        { value: "none", label: "None" },
        { value: "australia", label: "Australia" },
        // { value: "canada", label: "Canada" },
        // { value: "france", label: "France" },
        { value: "united-kingdom", label: "United Kingdom" },
        // { value: "united-states", label: "United States" },
        { value: "new-zealand", label: "New Zealand" },
        { value: "singapore", label: "Singapore" },
      ],
    },
  },
};

//courseInfoFilter.tsx
export interface CourseInfoFilterProps {
  selectedFieldOfStudy: string;
  setSelectedFieldOfStudy: (value: string) => void;
  selectedMajor: string;
  setSelectedMajor: (value: string) => void;
  selectedLevel: string;
  setSelectedLevel: (value: string) => void;
  fieldOfStudyOptions: FilterOption[];
  majorOptions: FilterOption[];
  levelOptions: FilterOption[];
}

//financialTimingFilter.tsx
export interface FinancialTimingFilterProps {
  feeBudget: number;
  setFeeBudget: (value: number) => void;
  fullTimeDuration: number;
  setFullTimeDuration: (value: number) => void;
  paymentSchemes: string;
  setPaymentSchemes: (value: string) => void;
  paymentSchemesOptions: FilterOption[];
  intakeMonth: string;
  setIntakeMonth: (value: string) => void;
  intakeMonthOptions: FilterOption[];
  partTimeOption: string;
  setPartTimeOption: (value: string) => void;
  partTimeOptions: FilterOption[];
  durationAbroad: string;
  setDurationAbroad: (value: string) => void;
  durationAbroadOptions: FilterOption[];
  maxFee: number;
  maxDuration: number;
}

//locationFilter.tsx
export interface LocationFilterProps {
  selectedCountry: string;
  setSelectedCountry: (value: string) => void;
  selectedState: string;
  setSelectedState: (value: string) => void;
  selectedInstitution: string;
  setSelectedInstitution: (value: string) => void;
  selectedEnvironment: string;
  setSelectedEnvironment: (value: string) => void;
  selectedPublicTransport: string;
  setSelectedPublicTransport: (value: string) => void;
  selectedOverseasTransfer: string;
  setSelectedOverseasTransfer: (value: string) => void;
  universityOptions: FilterOption[];
  countryOptions: FilterOption[];
  stateOptions: FilterOption[];
  overseasTransferOptions: FilterOption[];
}
